import { Plus, Trash2 } from 'lucide-react';
import { Button, HelpTooltip, Select } from '@fiado/react-kit';
import { Label } from '../../../components/ui/label';
import { Banner } from '../../../components/ui/Banner';
// PROPUESTA AL KIT (DEC-RBAC-045): el kit aún NO exporta un Switch. Se reusa el Switch local (Radix) que
// ya consumen SecurityPolicyEditor y DefaultsStep, mismo precedente. Pendiente de OK de Andres para
// promoverlo a @fiado/react-kit y migrar estos usos.
import { Switch } from '../../../components/ui/switch';
import type { Scope } from '../../../api/types/permissions';
import type { TenantLevelLocal } from '../../../api/types/PlatformLocal';
import { isLevelName, fallbackLevelDisplayName } from './levels/levelConfig';
import { OTHER_SCOPE_NAME, UNKNOWN_ROLE, WHOLE_ORGANIZATION } from '../../../lib/labelConstants';
import type { TenantRoleListItemLocal } from '../../../api/types/TenantLocal';
import type {
    AutoregisterAllowedRoleLocal,
    UpdateTenantAutoregisterRequestLocal,
} from '../../../api/types/AutoregisterLocal';

export interface AutoregisterDraft {
    enabled: boolean;
    allowedRoles: AutoregisterAllowedRoleLocal[];
    /** TTL del OTP en segundos. Vacío ('') = no se envía → el backend usa su default global. */
    otpTtlSeconds: number | '';
}

export const EMPTY_AUTOREGISTER: AutoregisterDraft = {
    enabled: false,
    allowedRoles: [],
    otpTtlSeconds: '',
};

/** Construye el body del PUT a partir del draft (omite `otpTtlSeconds` si quedó vacío). */
export function autoregisterDraftToBody(
    draft: AutoregisterDraft,
): UpdateTenantAutoregisterRequestLocal {
    const body: UpdateTenantAutoregisterRequestLocal = {
        enabled: draft.enabled,
        allowedRoles: draft.allowedRoles,
    };
    if (draft.otpTtlSeconds !== '') body.otpTtlSeconds = draft.otpTtlSeconds;
    return body;
}

/**
 * roleIds de `allowedRoles` que NO corresponden a ningún rol del tenant. El backend los rechaza
 * (validación anti-escalación DEC-RBAC-038: cada rol ⊆ roles del tenant), así que el modal GATEA el
 * submit con esto y se advierte inline. Solo aplica con autoregistro prendido.
 */
export function unknownAutoregisterRoleIds(
    draft: AutoregisterDraft,
    tenantRoles: TenantRoleListItemLocal[],
): string[] {
    if (!draft.enabled) return [];
    const known = new Set(tenantRoles.map((r) => r.roleId));
    return draft.allowedRoles.map((r) => r.roleId).filter((roleId) => !known.has(roleId));
}

/** Alcances ofrecidos: toda la organización y cada nivel de su aplicación, por nombre. */
export function autoregisterScopeOptions(
    levels: TenantLevelLocal[] = [],
): { value: Scope; label: string }[] {
    return [
        { value: 'TENANT', label: WHOLE_ORGANIZATION },
        ...levels.map((l) => ({ value: l.level, label: l.displayName })),
    ];
}

/** Nombre de un alcance que no está entre las opciones: nivel genérico o «Otro alcance». */
function fallbackScopeName(scope: Scope): string {
    return isLevelName(scope) ? fallbackLevelDisplayName(scope) : OTHER_SCOPE_NAME;
}

interface Props {
    value: AutoregisterDraft;
    onChange: (next: AutoregisterDraft) => void;
    /** Roles del tenant (fuente de los roleId del autoregistro). Anti-id: se muestra el nombre. */
    tenantRoles: TenantRoleListItemLocal[];
    /** Niveles de la aplicación de la organización (alcances ofrecidos además de toda la organización). */
    levels?: TenantLevelLocal[];
    rolesLoading?: boolean;
    rolesError?: boolean;
    disabled?: boolean;
}

/**
 * Sección Autoregistro del modal «Editar organización». Controlada (value/onChange), reusa el
 * patrón de edición de `allowedRoles` del DefaultsStep del wizard de aplicaciones: lista de filas
 * rol+alcance, agregar/quitar, con advertencia inline si un rol no existe en el tenant. Anti-id: muestra
 * el `roleName` del rol, nunca el roleId crudo.
 */
export function EditTenantAutoregisterSection({
    value,
    onChange,
    tenantRoles,
    levels,
    rolesLoading,
    rolesError,
    disabled,
}: Props) {
    const patch = (p: Partial<AutoregisterDraft>): void => {
        onChange({ ...value, ...p });
    };

    // Roles ofrecidos por nombre (anti-id), alfabético (default del proyecto). roleId queda tras el label.
    const roleOptions = [...tenantRoles]
        .sort((a, b) => a.roleName.localeCompare(b.roleName, 'es'))
        .map((r) => ({ value: r.roleId, label: r.roleName }));
    const scopeOptions = autoregisterScopeOptions(levels);
    const roleLabel = (roleId: string): string =>
        tenantRoles.find((r) => r.roleId === roleId)?.roleName ?? UNKNOWN_ROLE;
    const scopeLabel = (scope: Scope): string =>
        scopeOptions.find((o) => o.value === scope)?.label ?? fallbackScopeName(scope);

    const unknown = unknownAutoregisterRoleIds(value, tenantRoles);

    const addRole = (): void => {
        // Guard explícito: sin roles del tenant no se puede agregar un allowedRole coherente.
        if (roleOptions.length === 0) return;
        const next: AutoregisterAllowedRoleLocal = {
            roleId: roleOptions[0].value,
            scope: 'TENANT',
        };
        patch({ allowedRoles: [...value.allowedRoles, next] });
    };
    const editRole = (i: number, p: Partial<AutoregisterAllowedRoleLocal>): void => {
        patch({
            allowedRoles: value.allowedRoles.map((r, idx) => (idx === i ? { ...r, ...p } : r)),
        });
    };
    const removeRole = (i: number): void => {
        patch({ allowedRoles: value.allowedRoles.filter((_, idx) => idx !== i) });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 rounded-md border p-4">
                <div className="space-y-0.5">
                    <Label htmlFor="autoreg-enabled" className="cursor-pointer font-medium">
                        Autoregistro
                    </Label>
                    <p className="text-xs text-muted-foreground">
                        Permite que los usuarios se registren solos con uno de los roles permitidos.
                        Por seguridad, solo el equipo de plataforma edita esta lista.
                    </p>
                </div>
                <Switch
                    id="autoreg-enabled"
                    checked={value.enabled}
                    disabled={disabled}
                    onCheckedChange={(checked) => {
                        patch({ enabled: checked });
                    }}
                    aria-label="Autoregistro"
                />
            </div>

            <fieldset
                className={value.enabled ? 'space-y-4' : 'space-y-4 pointer-events-none opacity-50'}
                disabled={!value.enabled || disabled}
                aria-disabled={!value.enabled || disabled}
            >
                <legend className="sr-only">Configuración de autoregistro</legend>

                {value.enabled && unknown.length > 0 && (
                    <Banner variant="warning">
                        Hay roles permitidos que no existen en esta organización
                        {`: ${unknown.map(roleLabel).join(', ')}`}. El sistema los rechaza.
                        Corrígelos o quítalos.
                    </Banner>
                )}

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                            <Label>Roles permitidos</Label>
                            <HelpTooltip content="Roles que un usuario puede asignarse al registrarse solo. Por seguridad, solo el equipo de plataforma edita esta lista." />
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            leftIcon={<Plus className="h-4 w-4" />}
                            disabled={!value.enabled || disabled || roleOptions.length === 0}
                            onClick={addRole}
                        >
                            Agregar rol
                        </Button>
                    </div>
                    {rolesLoading ? (
                        <p className="text-xs text-muted-foreground">Cargando roles…</p>
                    ) : rolesError ? (
                        <p className="text-xs text-destructive">
                            No pudimos cargar los roles de la organización.
                        </p>
                    ) : roleOptions.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            Esta organización no tiene roles. Crea roles primero.
                        </p>
                    ) : value.allowedRoles.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            Agrega al menos un rol que pueda usarse en el autoregistro.
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {value.allowedRoles.map((r, i) => {
                                const isUnknown = unknown.includes(r.roleId);
                                return (
                                    <li
                                        key={`${r.roleId}-${String(i)}`}
                                        data-testid={`autoreg-role-${String(i)}`}
                                        className="grid gap-2 rounded-md border px-3 py-2 sm:grid-cols-[1fr_1fr_auto]"
                                    >
                                        <Select
                                            label="Rol"
                                            value={r.roleId}
                                            error={isUnknown ? 'Rol desconocido' : undefined}
                                            disabled={disabled}
                                            onChange={(v) => {
                                                editRole(i, { roleId: v });
                                            }}
                                            options={roleOptions}
                                        />
                                        <Select
                                            label="Alcance"
                                            value={r.scope}
                                            disabled={disabled}
                                            onChange={(v) => {
                                                editRole(i, { scope: v as Scope });
                                            }}
                                            options={scopeOptions}
                                        />
                                        <div className="flex items-end">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                disabled={disabled}
                                                aria-label={`Quitar rol ${roleLabel(r.roleId)} (${scopeLabel(r.scope)})`}
                                                onClick={() => {
                                                    removeRole(i);
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="max-w-xs space-y-1.5">
                    <div className="flex items-center gap-1">
                        <Label htmlFor="autoreg-otp-ttl">Vigencia del código (segundos)</Label>
                        <HelpTooltip content="Segundos que el código de un solo uso del autoregistro sigue siendo válido." />
                    </div>
                    <input
                        id="autoreg-otp-ttl"
                        type="number"
                        min={1}
                        inputMode="numeric"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Valor predeterminado del sistema"
                        disabled={disabled}
                        value={value.otpTtlSeconds}
                        onChange={(e) => {
                            patch({
                                otpTtlSeconds: e.target.value === '' ? '' : Number(e.target.value),
                            });
                        }}
                    />
                    <p className="text-xs text-muted-foreground">
                        Cuánto vale el código de un solo uso del autoregistro. Si lo dejas vacío, se
                        usa el valor predeterminado del sistema.
                    </p>
                </div>
            </fieldset>
        </div>
    );
}
